import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAiModelList } from "@/hooks/use-ai";
import type { AiModelDraft, AiProviderInfo } from "@/lib/callables";

const CUSTOM_VALUE = "__outro__";
/** A espera depois da última tecla da chave, antes de consultar o provedor. */
const KEY_DEBOUNCE_MS = 700;

interface AiModelSelectProps {
  inputId: string;
  provider: AiProviderInfo;
  model: string;
  customModel: boolean;
  /** A etiqueta da chave do item (`credentialTag`), ou `null` sem chave. */
  credential: string | null;
  models: AiModelDraft[];
  index: number;
  onChange: (patch: { model: string; customModel: boolean }) => void;
}

/**
 * O modelo de um item da fila: a lista consultada agora no provedor, com a
 * chave do item, e o catálogo do Zé Padoca quando a consulta não dá. A opção
 * "Outro modelo" abre o campo de texto para o que a lista não tem.
 */
export function AiModelSelect({
  inputId,
  provider,
  model,
  customModel,
  credential,
  models,
  index,
  onChange,
}: AiModelSelectProps) {
  // A chave digitada muda a cada tecla: a consulta espera a pessoa parar.
  const [debounced, setDebounced] = useState(credential);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(credential), KEY_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [credential]);

  const list = useAiModelList({ provider: provider.id, credential: debounced, models, index });
  const options = list.data?.models ?? provider.catalog;
  // O modelo salvo aparece mesmo quando o provedor não o lista mais.
  const withCurrent =
    model && !customModel && !options.some((option) => option.id === model)
      ? [{ id: model, label: `${model} (atual)` }, ...options]
      : options;

  const caption = list.isFetching
    ? "Consultando a lista do provedor..."
    : list.data?.live
      ? "Lista consultada agora no provedor."
      : debounced === null
        ? "Lista padrão do Zé Padoca. Informe a chave para consultar a do provedor."
        : `Lista padrão do Zé Padoca: ${list.data?.error ?? "a consulta ao provedor falhou."}`;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId}>Modelo</Label>
      <Select
        value={customModel ? CUSTOM_VALUE : model}
        onValueChange={(value) =>
          value === CUSTOM_VALUE
            ? onChange({ model: "", customModel: true })
            : onChange({ model: value, customModel: false })
        }
      >
        <SelectTrigger id={inputId} aria-label="Modelo">
          <SelectValue placeholder="Escolha o modelo" />
        </SelectTrigger>
        <SelectContent>
          {withCurrent.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
          <SelectItem value={CUSTOM_VALUE}>Outro modelo (digitar o nome)</SelectItem>
        </SelectContent>
      </Select>
      {customModel ? (
        <Input
          aria-label="Nome do modelo"
          value={model}
          onChange={(event) => onChange({ model: event.target.value, customModel: true })}
          placeholder={provider.defaultModel}
          className="font-mono"
          autoComplete="off"
          spellCheck={false}
        />
      ) : null}
      <p className="flex items-center gap-1.5 text-caption text-muted-foreground">
        {list.isFetching ? <Loader2 className="size-3 animate-spin" /> : null}
        <span>{caption}</span>
      </p>
    </div>
  );
}
